import moment from 'moment';

// NOTE: do NOT import router-x/routes here — that creates a circular
// routes → pages → executive → utils → routes dependency that white-screens the whole CRA app (including /auth).

/** Executive date presets use Asia/Karachi business day (matches snapshot_date). */
const BUSINESS_TZ = 'Asia/Karachi';

const karachiDateKey = (d = new Date()) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d instanceof Date ? d : new Date(d));

const karachiMonthStart = (d = new Date()) => {
  const key = karachiDateKey(d);
  return `${key.slice(0, 7)}-01`;
};

/** Subtract calendar days from a YYYY-MM-DD key (UTC noon trick avoids DST edge). */
const addDaysKey = (dateKey, deltaDays) => {
  const [y, m, day] = dateKey.split('-').map(Number);
  const utc = new Date(Date.UTC(y, m - 1, day + deltaDays, 12, 0, 0));
  return karachiDateKey(utc);
};

export const PRESETS = {
  today: 'Today',
  last7: 'Last 7 days',
  mtd: 'MTD',
  custom: 'Custom',
};


/** PRD checklist section titles — keep labels searchable for QA. */
export const CC_SECTION_TITLES = {
  commandBar: "Command bar · Heroes & sparklines",
  needAttention: "Need Attention",
  capacity: "Capacity & Sites · Launchpad · Nexus",
  revenue: "Revenue waterfall · Pulse invoices × Zoho cash",
  economics: "Per-seat · Scenario · Churn",
  opsHeat: "Ops heat · teasers only",
  marketing: "Marketing · Budget · Ask",
};

/** Subheadings that must appear inside sections (not only in prose). */
export const CC_SUBHEADINGS = {
  launchpadVsNexus: "Launchpad vs Nexus",
  sixMoTrends: "6-mo trends",
  perSeat: "Per-seat economics",
  churn: "Churn & freeze watch",
  revenueWaterfall: "Revenue waterfall",
};


export const defaultMtdRange = () => ({
  preset: 'mtd',
  from: karachiMonthStart(),
  to: karachiDateKey(),
});

/** Last ~6 calendar months ending today (Karachi) — for trends/sparklines. */
export const lastSixMonthsRange = () => {
  const to = karachiDateKey();
  return { from: addDaysKey(to, -182), to, preset: 'custom' };
};

export const rangeForPreset = (preset, from, to) => {
  const today = karachiDateKey();
  if (preset === 'today') return { from: today, to: today, preset };
  if (preset === 'last7') {
    return {
      from: addDaysKey(today, -6),
      to: today,
      preset,
    };
  }
  if (preset === 'mtd') {
    return {
      from: karachiMonthStart(),
      to: today,
      preset,
    };
  }
  return { from, to, preset: 'custom' };
};

/** Agha: always full grouped digits (no 1.8M / 4.2K compact). */
export const formatPkrFull = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString('en-PK', { maximumFractionDigits: 0 });
};

// Agha: all amounts full en-PK digits with separators — no M/K compact.
export const formatPkrCompact = (value) => formatPkrFull(value);

export const formatPct = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `${Number(value).toFixed(1)}%`;
};

export const formatSynced = (iso) => {
  if (!iso) return '—';
  return moment(iso).format('YYYY-MM-DD HH:mm');
};

/** Drill-through helpers — open Pulse modules with prefilters */
export const drillLinks = {
  supportOpen: '/support?status=open',
  supportSla: '/support?status=open&sla=breach',
  maintenanceOpen: '/maintenance?status=open',
  maintenanceOverdue: '/maintenance?status=overdue',
  customersActive: '/customer?f:customerProfile.inactive:boolean=false',
  customersFrozen: '/customer?f:customerProfile.frozen:boolean=true',
  inventoryLow: '/inventory?tab=items&stock=low',
  meetings: '/meeting-room',
  seats: '/seat',
};

export const LEAD_FUNNEL_ORDER = [
  { key: 'new', label: 'New', color: '#0d9488' },
  { key: 'contacted', label: 'Contacted', color: '#14b8a6' },
  { key: 'qualified', label: 'Qualified', color: '#0891b2' },
  { key: 'negotiating', label: 'Negotiating', color: '#6366f1' },
  { key: 'open', label: 'Open', color: '#64748b' },
  { key: 'won', label: 'Won', color: '#059669' },
];

/** Cap Need Attention cards (PRD: max 3–5). */
export const capAttention = (flags, max = 5) => {
  const list = Array.isArray(flags) ? flags : [];
  const rank = (s) => (s === 'critical' ? 0 : 1);
  return [...list]
    .sort((a, b) => rank(a?.severity) - rank(b?.severity))
    .slice(0, Math.max(0, max));
};

export const severityTag = (severity) => {
  if (severity === 'critical') return { text: 'HIGH', warn: false };
  return { text: 'MED', warn: true };
};

/** Find Launchpad / Nexus grain from snapshot.buildings[]. */
export const findBuilding = (buildings, name) => {
  const list = Array.isArray(buildings) ? buildings : [];
  const needle = String(name || '').toLowerCase();
  return list.find((b) => String(b?.name || '').toLowerCase().includes(needle)) || null;
};

/**
 * Growth funnel steps from Pulse leads + occupied seats.
 * Never invents counts — missing keys show 0.
 */
export const buildGrowthFunnel = (leads = {}, occupiedSeats = null) => {
  const by = leads?.byStatus || {};
  const open =
    (by.open || 0) +
    (by.new || 0) +
    (by.contacted || 0) +
    (by.qualified || 0) +
    (by.negotiating || 0);
  const contacted = by.contacted || 0;
  const won = by.won || 0;
  const steps = [
    { key: 'open', label: 'Leads open', count: open || leads?.total || 0 },
    { key: 'contacted', label: 'Contacted', count: contacted },
    { key: 'won', label: 'Won', count: won },
    {
      key: 'occupied',
      label: 'Occupied seats',
      count: occupiedSeats == null ? null : occupiedSeats,
    },
  ];
  const numeric = steps.map((s) => s.count).filter((n) => n != null && n > 0);
  const max = Math.max(...numeric, 1);
  return steps.map((s) => ({
    ...s,
    widthPct: s.count == null ? 0 : Math.max(12, Math.round((Number(s.count) / max) * 100)),
  }));
};

/** Revenue waterfall column heights (relative). Null amounts → empty. */
export const buildWaterfall = ({ invoiced, collected, arUnpaid } = {}) => {
  const vals = [invoiced, collected, arUnpaid].map((v) => (v == null || Number.isNaN(Number(v)) ? null : Number(v)));
  const max = Math.max(...vals.filter((v) => v != null && v > 0), 1);
  const cols = [
    { key: 'invoiced', label: 'Invoiced', amount: vals[0], color: '#0B1F33', source: 'Pulse' },
    { key: 'collected', label: 'Collected', amount: vals[1], color: '#0D9488', source: 'Zoho' },
    { key: 'ar', label: 'AR unpaid', amount: vals[2], color: '#F59E0B', source: 'Zoho' },
  ];
  return cols.map((c) => ({
    ...c,
    heightPct: c.amount == null ? null : Math.max(8, Math.round((Math.max(0, c.amount) / max) * 100)),
  }));
};

/** Invoice rent ÷ occupied seats — null when inputs missing (never invent). */
export const rentPerOccupiedSeat = (rentPkr, occupiedSeats) => {
  if (rentPkr == null || occupiedSeats == null) return null;
  const occ = Number(occupiedSeats);
  if (!occ || Number.isNaN(Number(rentPkr))) return null;
  return Number(rentPkr) / occ;
};

/**
 * Fill-N-vacant scenario using live avg rent/seat when available.
 * Returns null if vacant or avg rent unknown — never invents rates.
 */
export const buildFillScenario = ({ vacant, rentPerSeat, fillN = 10 } = {}) => {
  if (vacant == null || rentPerSeat == null || Number.isNaN(Number(rentPerSeat))) return null;
  const n = Math.min(Number(fillN) || 10, Math.max(0, Number(vacant) || 0));
  if (n <= 0) return null;
  return {
    fillN: n,
    rentPerSeat: Number(rentPerSeat),
    monthlyUplift: n * Number(rentPerSeat),
  };
};

/** Numeric series → SVG polyline points string for sparklines. */
export const sparklinePoints = (values, width = 100, height = 28, pad = 2) => {
  const nums = (Array.isArray(values) ? values : [])
    .map((v) => (v == null || Number.isNaN(Number(v)) ? null : Number(v)))
    .filter((v) => v != null);
  if (nums.length < 2) return '';
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const span = max - min || 1;
  const innerH = height - pad * 2;
  const step = width / (nums.length - 1);
  return nums
    .map((v, i) => {
      const x = i * step;
      const y = pad + innerH - ((v - min) / span) * innerH;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
};

/** Delta helper between last two numeric points — null if insufficient. */
export const seriesDelta = (values) => {
  const nums = (Array.isArray(values) ? values : [])
    .map((v) => (v == null || Number.isNaN(Number(v)) ? null : Number(v)))
    .filter((v) => v != null);
  if (nums.length < 2) return null;
  return nums[nums.length - 1] - nums[nums.length - 2];
};

/**
 * Build command-bar heroes from live aggregate payload + optional trend series.
 * Never invents Zoho/Pulse numbers — uses — / empty spark when missing.
 */
export const buildCommandHeroes = (data, trendSeries = []) => {
  const snap = data?.snapshot || {};
  const seats = snap.seats || {};
  const customers = snap.customers || {};
  const money = data?.money || {};
  const collections = money.collections?.receivedPkr;
  const series = Array.isArray(trendSeries) ? trendSeries : [];

  const pick = (key) => series.map((p) => p?.[key]);

  const heroes = [
    {
      key: 'occupancy',
      label: 'Occupancy',
      value: seats.occupancyPct == null ? null : formatPct(seats.occupancyPct),
      raw: seats.occupancyPct,
      spark: sparklinePoints(pick('occupancyPct')),
      sparkColor: '#0D9488',
      delta: seriesDelta(pick('occupancyPct')),
      deltaSuffix: ' pp',
      hint: 'Valencia · seats',
    },
    {
      key: 'vacant',
      label: 'Vacant seats',
      value: seats.vacant == null ? null : String(seats.vacant),
      raw: seats.vacant,
      spark: sparklinePoints(pick('vacantSeats')),
      sparkColor: '#D97706',
      delta: seriesDelta(pick('vacantSeats')),
      deltaSuffix: '',
      hint: 'Operational vacant',
    },
    {
      key: 'invoiceRent',
      label: 'Invoice rent',
      value: snap.rent?.dashboardRentPkr == null ? null : `PKR ${formatPkrFull(snap.rent.dashboardRentPkr)}`,
      raw: snap.rent?.dashboardRentPkr,
      spark: sparklinePoints(pick('dashboardRent')),
      sparkColor: '#0B1F33',
      delta: null,
      hint: 'Pulse invoices',
    },
    {
      key: 'cashCollected',
      label: 'Cash collected',
      value: collections == null ? null : `PKR ${formatPkrFull(collections)}`,
      raw: collections,
      spark: sparklinePoints(pick('collectionsReceived')),
      sparkColor: '#059669',
      delta: null,
      hint: money.collections?.note || 'Zoho payments',
      degraded: collections == null,
    },
    {
      key: 'activeCustomers',
      label: 'Active customers',
      value: customers.active == null ? null : String(customers.active),
      raw: customers.active,
      spark: sparklinePoints(pick('activeCustomers')),
      sparkColor: '#14324F',
      delta: seriesDelta(pick('activeCustomers')),
      deltaSuffix: '',
      hint: customers.total != null ? `of ${customers.total} total` : 'Pulse customers',
    },
    {
      key: 'openRisks',
      label: 'Open risks',
      value: data?.openRisks != null
        ? String(data.openRisks)
        : String((data?.needAttention || []).length),
      raw: data?.openRisks ?? (data?.needAttention || []).length,
      spark: sparklinePoints(pick('openRisks')),
      sparkColor: '#DC2626',
      delta: null,
      hint: 'Need Attention',
      danger: true,
    },
  ];
  return heroes;
};

export const formatDelta = (delta, suffix = '') => {
  if (delta == null || Number.isNaN(Number(delta))) return null;
  const n = Number(delta);
  if (Math.abs(n) < 0.05) return { text: `flat${suffix ? ` · ${suffix}` : ''}`, tone: 'flat' };
  const arrow = n > 0 ? '▲' : '▼';
  const tone = n > 0 ? 'up' : 'down';
  const abs = Math.abs(n);
  const shown = Number.isInteger(abs) ? String(abs) : abs.toFixed(1);
  return { text: `${arrow} ${shown}${suffix}`, tone };
};


/** Format signed PKR variance for BvA tables (en-PK full digits). */
export const formatSignedPkr = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const n = Number(value);
  const abs = Math.abs(n).toLocaleString('en-PK', { maximumFractionDigits: 0 });
  if (n > 0) return `+${abs}`;
  if (n < 0) return `-${abs}`;
  return abs;
};

/** Tone class for variance cells. */
export const varianceTone = (value) => {
  if (value == null || Number.isNaN(Number(value))) return 'flat';
  const n = Number(value);
  if (n > 0) return 'up';
  if (n < 0) return 'down';
  return 'flat';
};

/**
 * WBP-180 CoA drill filter.
 * Keep budget≠0 OR expense≠0 (defense in depth — BE already filters).
 * categoryFilter: null/'all' → all; named category; 'Other' → uncategorized/Other.
 */
export const filterCoaDrillAccounts = (accounts, categoryFilter = null) => {
  const list = Array.isArray(accounts) ? accounts : [];
  const nonZero = list.filter((a) => {
    const b = Number(a?.budgetPkr);
    const e = Number(a?.expensePkr);
    const budget = Number.isFinite(b) ? b : 0;
    const expense = Number.isFinite(e) ? e : 0;
    return budget !== 0 || expense !== 0;
  });
  if (!categoryFilter || categoryFilter === 'all') return nonZero;
  if (categoryFilter === 'Other') {
    return nonZero.filter((a) => !a?.category || a.category === 'Other');
  }
  return nonZero.filter((a) => a?.category === categoryFilter);
};

/** Account under-posted when budget>0 and expense==0 (or BE flag). */
export const isCoaUnderPosted = (account) => {
  if (account?.underPosted === true) return true;
  const b = Number(account?.budgetPkr);
  const e = Number(account?.expensePkr);
  return (Number.isFinite(b) ? b : 0) > 0 && (Number.isFinite(e) ? e : 0) === 0;
};

/** Display label for CoA account: "Code · Name" or name only. */
export const formatCoaAccountLabel = (account) => {
  const name = account?.name || '—';
  const code = account?.code;
  if (code) return `${code} · ${name}`;
  return name;
};

/** Human month label from YYYY-MM (e.g. 2026-09 → Sep 2026). Locale-stable. */
const BVA_MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const formatBvaMonthLabel = (yyyyMm) => {
  if (!yyyyMm || String(yyyyMm).length < 7) return '';
  const [y, m] = String(yyyyMm).slice(0, 7).split('-').map(Number);
  if (!y || !m || m < 1 || m > 12) return String(yyyyMm);
  return `${BVA_MONTH_SHORT[m - 1]} ${y}`;
};
